import type { LoadDTO, LoadVehicleInspectionDTO } from '../data/LoadDTO';
import {
  getInspectablePhotoSyncCounts,
  getInspectableUrlsFromVideo,
  isDefaultInspectionPhoto,
  type InspectablePhotoUrls,
} from './view-load-inspection/inspectionPhotoSync';

export function getLoadInspectionList(
  load: Partial<Pick<LoadDTO, 'inspection_info' | 'inspection_photos'>>,
): LoadVehicleInspectionDTO[] {
  if (load.inspection_info && load.inspection_info.length > 0) {
    return load.inspection_info;
  }

  if (load.inspection_photos && load.inspection_photos.length > 0) {
    return load.inspection_photos;
  }

  return [];
}

export function getInspectablePhotosFromVehicle(
  vehicle: LoadVehicleInspectionDTO,
): InspectablePhotoUrls[] {
  const inspectionPhotos = (vehicle.photos ?? [])
    .filter((photo) => !isDefaultInspectionPhoto(photo))
    .map((photo) => ({
      url: photo.rendered_photo,
      thumbnailUrl: photo.rendered_photo_thumbnail,
    }));

  const aiagDamagePhotos = (vehicle.aiag_inspections ?? []).flatMap(
    (inspection) =>
      (inspection.damages ?? []).flatMap((damage) =>
        (damage.photos ?? []).map((photo) => ({
          url: photo.original_photo_url,
          thumbnailUrl: photo.photo_thumbnail_url,
        })),
      ),
  );

  const inspectionVideos = (vehicle.videos ?? []).map(
    getInspectableUrlsFromVideo,
  );

  return [...inspectionPhotos, ...aiagDamagePhotos, ...inspectionVideos];
}

export function vehicleHasOnlyDefaultInspectionPhotos(
  vehicle: LoadVehicleInspectionDTO,
): boolean {
  const photos = vehicle.photos ?? [];
  if (photos.length === 0) {
    return false;
  }

  if (!photos.every((photo) => isDefaultInspectionPhoto(photo))) {
    return false;
  }

  const hasVideos = (vehicle.videos ?? []).length > 0;
  const hasAiagDamagePhotos = (vehicle.aiag_inspections ?? []).some(
    (inspection) =>
      (inspection.damages ?? []).some((damage) => (damage.photos ?? []).length),
  );

  return !hasVideos && !hasAiagDamagePhotos;
}

export function shouldShowVehicleInspectionSyncSummary(
  vehicle: LoadVehicleInspectionDTO,
): boolean {
  if (vehicleHasOnlyDefaultInspectionPhotos(vehicle)) {
    return false;
  }

  return getVehicleInspectionPhotoSyncCounts(vehicle).total > 0;
}

export function getVehicleInspectionPhotoSyncCounts(
  vehicle: LoadVehicleInspectionDTO,
): { synced: number; total: number } {
  return getInspectablePhotoSyncCounts(
    getInspectablePhotosFromVehicle(vehicle),
  );
}

export function getLoadInspectionPhotoSyncCountsFromLoad(
  load: Partial<Pick<LoadDTO, 'inspection_info' | 'inspection_photos'>>,
): { synced: number; total: number } {
  const list = getLoadInspectionList(load);
  const allPhotos = list.flatMap((vehicle) =>
    getInspectablePhotosFromVehicle(vehicle),
  );
  return getInspectablePhotoSyncCounts(allPhotos);
}
